import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { createSubscriptionCheckoutApi } from "services/postApis";
import { getUserProfileApi } from "services/authService";
import { ErrorHandler, ErrorToast } from "utils/toast";
import ButtonLoader from "components/ButtonLoader";
import { useRedirectTo } from "hooks/useRedirectTo";
import "../styles/Subscription.css";
import sectionLabel from "../assets/icons/section_label.svg";
import btnArrow from "../assets/icons/btn_arrow.svg";

import { useQuery } from "@tanstack/react-query";

const subscriptionBenefits = [
  {
    icon: "🌿",
    title: "Monthly Practitioner Review",
    desc: "Your health progress and formula effectiveness is reviewed each month by a licensed TCM practitioner.",
  },
  {
    icon: "🔄",
    title: "Formula Adjustments",
    desc: "Updated herbal recommendations as your symptoms shift and your body responds to treatment.",
  },
  {
    icon: "🌱",
    title: "Ongoing Care & Lifestyle Guidance",
    desc: "Continuous lifestyle recommendations tailored to your evolving wellness journey.",
  },
  {
    icon: "📦",
    title: "Monthly Herbal Shipment",
    desc: "One personalized herbal formula shipment delivered directly to your door every month at $0 additional cost.",
  },
  {
    icon: "💻",
    title: "Web Dashboard Access",
    desc: "View your current recommendations and care plan anytime through your personal web dashboard.",
  },
  {
    icon: "📧",
    title: "Full History via Email",
    desc: "Receive your full recommendation history and care updates delivered directly to your inbox.",
  },
];

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { user, loading, updateUser, isAuthenticated } = useAuth();
  const { handleManageSubscription } = useRedirectTo();
  const [isEnrolling, setIsEnrolling] = useState(false);

  // Fetch latest profile on subscription page mount to ensure fresh eligibility
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ["userProfile"],
    queryFn: getUserProfileApi,
    enabled: Boolean(isAuthenticated),
    staleTime: 1000 * 60 * 2,
  });

  useEffect(() => {
    if (profileData?.payload) {
      updateUser(profileData.payload);
    }
  }, [profileData, updateUser]);

  useEffect(() => {
    if (
      !loading &&
      !profileLoading &&
      profileData &&
      !profileData?.payload?.isEligibleForSubscription &&
      !user?.isEligibleForSubscription
    ) {
      navigate("/", { replace: true });
    }
  }, [
    user?.isEligibleForSubscription,
    profileData,
    loading,
    profileLoading,
    navigate,
  ]);

  const isAlreadySubscribed = Boolean(
    user?.isAlreadySubscribed || user?.isSubscribed
  );

  const handleEnrollNow = async () => {
    if (isEnrolling) return;
    try {
      setIsEnrolling(true);
      const res = await createSubscriptionCheckoutApi();
      const checkoutUrl =
        res?.payload?.checkoutUrl ||
        res?.data?.payload?.checkoutUrl ||
        res?.checkoutUrl;

      if (checkoutUrl) {
        localStorage.setItem("isGoForSubscription", "true");
        window.location.href = checkoutUrl;
      } else if (res?.payload?.isAlreadySubscribed) {
        ErrorToast("You are already subscribed.");
        const profile = await getUserProfileApi();
        if (profile?.payload) {
          updateUser(profile.payload);
        }
      } else {
        ErrorToast(res?.message || "Failed to create checkout session.");
      }
    } catch (error) {
      ErrorHandler(error);
    } finally {
      setIsEnrolling(false);
    }
  };

  return (
    <div className="subscription-page">
      {/* ---- Hero ---- */}
      <section className="subscription-hero">
        {/* Badge — same pattern as hero-badge */}
        <div className="subscription-badge">
          <span className="subscription-badge-text">
            {isAlreadySubscribed
              ? "Active Subscription"
              : "Monthly Subscription Program"}
          </span>
        </div>

        <h1 className="subscription-hero-title">
          Ongoing herbal care,{" "}
          <em>tailored to you.</em>
        </h1>

        <p className="subscription-hero-subtitle">
          {isAlreadySubscribed
            ? "Your ongoing healing journey with monthly practitioner reviews, custom formula adjustments, and continuous personalized herbal shipments."
            : "Continue your healing journey with monthly practitioner reviews, formula adjustments, and personalized herbal shipments — all for one simple monthly fee."}
        </p>

        {/* Price */}
        <div className="subscription-price-block">
          <div className="subscription-price-main">
            <span className="subscription-price-currency">$</span>
            <span className="subscription-price-amount">149</span>
          </div>
          <span className="subscription-price-period">
            per month · cancel anytime
          </span>
        </div>

        {isAlreadySubscribed ? (
          <button
            id="subscription-manage-hero-btn"
            className="subscription-cta-btn"
            onClick={handleManageSubscription}
          >
            Manage Subscription
            <img src={btnArrow} alt="" width="20" height="20" />
          </button>
        ) : (
          <button
            id="subscription-enroll-hero-btn"
            className="subscription-cta-btn"
            onClick={handleEnrollNow}
            disabled={isEnrolling}
          >
            {isEnrolling ? (
              <ButtonLoader size="sm" color="dark" />
            ) : (
              <>
                Enroll Now
                <img src={btnArrow} alt="" width="20" height="20" />
              </>
            )}
          </button>
        )}
      </section>

      {/* ---- What's Included ---- */}
      <section className="subscription-includes">
        <div className="subscription-section-header">
          {/* Section label image — same as CTASection & BenefitsSection */}
          <div className="subscription-section-label">
            <img src={sectionLabel} alt="" width="192" height="32" />
          </div>
          <h2 className="subscription-section-title">
            Everything you need for long-term wellness
          </h2>
          <p className="subscription-section-subtitle">
            Your subscription keeps your care active and evolving — so your
            formula always matches where you are in your healing journey.
          </p>
        </div>

        <div className="subscription-benefits-grid">
          {subscriptionBenefits.map((benefit, idx) => (
            <div className="subscription-benefit-card" key={idx}>
              <div className="subscription-benefit-icon">{benefit.icon}</div>
              <h3 className="subscription-benefit-title">{benefit.title}</h3>
              <p className="subscription-benefit-desc">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Eligibility & Enrollment (for non-subscribers) vs Your Monthly Care Cycle (for active subscribers) ---- */}
      <section className="subscription-eligibility">
        <div className="subscription-eligibility-card">
          {/* Section label — same as home page pattern (inside section, above title) */}
          <div className="subscription-section-label-divider">
            <img src={sectionLabel} alt="" width="192" height="32" />
          </div>

          {isAlreadySubscribed ? (
            <>
              <h2 className="subscription-eligibility-title">
                Your Monthly Care Cycle
              </h2>

              <div className="subscription-eligibility-steps">
                <div className="subscription-eligibility-step">
                  <div className="subscription-step-number">1</div>
                  <p className="subscription-step-text">
                    <strong>Monthly Check-In Email:</strong> Each month, you receive an email with a direct check-in button that takes you to your monthly check-in form to share your progress.
                  </p>
                </div>
                <div className="subscription-eligibility-step">
                  <div className="subscription-step-number">2</div>
                  <p className="subscription-step-text">
                    <strong>Form Submission &amp; Review:</strong> Fill out and submit your check-in form, and your submitted form is reviewed.
                  </p>
                </div>
                <div className="subscription-eligibility-step">
                  <div className="subscription-step-number">3</div>
                  <p className="subscription-step-text">
                    <strong>Herbs Delivered at $0 Cost:</strong> After review, your tailored herbs will be shipped to you at $0 cost.
                  </p>
                </div>
              </div>

              <p className="subscription-eligibility-note">
                Your monthly care cycle covers form reviews and your herbal deliveries at $0 additional cost. Manage your subscription anytime from your dashboard.
              </p>
            </>
          ) : (
            <>
              <h2 className="subscription-eligibility-title">
                Eligibility &amp; Enrollment
              </h2>

              <div className="subscription-eligibility-steps">
                <div className="subscription-eligibility-step">
                  <div className="subscription-step-number">1</div>
                  <p className="subscription-step-text">
                    Complete the{" "}
                    <strong>Standard Intake ($179)</strong> or{" "}
                    <strong>Zoom Consultation ($275)</strong>
                  </p>
                </div>
                <div className="subscription-eligibility-step">
                  <div className="subscription-step-number">2</div>
                  <p className="subscription-step-text">
                    Receive your personalized herbal plan and enroll in{" "}
                    <strong>ongoing monthly care</strong>
                  </p>
                </div>
                <div className="subscription-eligibility-step">
                  <div className="subscription-step-number">3</div>
                  <p className="subscription-step-text">
                    Manage your subscription anytime —{" "}
                    <strong>cancel or update payment</strong> from your account
                    dashboard
                  </p>
                </div>
              </div>

              <p className="subscription-eligibility-note">
                Recurring billing of $149/month via your saved payment method.
                Cancel anytime from your account dashboard. No hidden fees.
              </p>
            </>
          )}
        </div>
      </section>

      {/* ---- Bottom CTA — same structure as CTASection ---- */}
      <section className="subscription-bottom-cta">
        <div className="subscription-bottom-cta-inner">
          <h2 className="subscription-bottom-cta-title">
            Ancient medicine.
            <br />
            <em>Tailored to the individual.</em>
          </h2>
          <p className="subscription-bottom-cta-sub">
            {isAlreadySubscribed
              ? "Your personalized care continues each month with ongoing formula refinement and practitioner guidance."
              : "Begin ongoing care and receive herbal medicine refined specifically for you each month."}
          </p>

          {isAlreadySubscribed ? (
            <button
              id="subscription-manage-bottom-btn"
              className="subscription-cta-btn"
              onClick={handleManageSubscription}
            >
              Manage Subscription
              <img src={btnArrow} alt="" width="20" height="20" />
            </button>
          ) : (
            <button
              id="subscription-enroll-bottom-btn"
              className="subscription-cta-btn"
              onClick={handleEnrollNow}
              disabled={isEnrolling}
            >
              {isEnrolling ? (
                <ButtonLoader size="sm" color="dark" />
              ) : (
                <>
                  Enroll Now — $149/month
                  <img src={btnArrow} alt="" width="20" height="20" />
                </>
              )}
            </button>
          )}

          <div className="subscription-bottom-divider" />

          <p className="subscription-price-note">
            $149/month · Recurring billing · Cancel anytime
          </p>
        </div>
      </section>
    </div>
  );
};

export default SubscriptionPage;
